import { Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {
  private loader: Loader;
  private googleMapsLoaded = false;
  
  // Cache de librerías importadas
  private placesLibrary: any = null;
  private mapsLibrary: any = null;
  private markerLibrary: any = null;

  constructor() {
    if (!environment.googleMapsApiKey) {
      console.warn('Google Maps API key no configurada. Defina environment.googleMapsApiKey.');
    }else{
      this.loader = new Loader({
        apiKey: environment.googleMapsApiKey,
        version: 'weekly'
      });
    }
  }

  //Carga Google Maps API
  async loadGoogleMaps(): Promise<void> {
    if (this.googleMapsLoaded) {
      return Promise.resolve();
    }
    try {
      // Importar librerías necesarias
      const [places, maps, marker] = await Promise.all([
        this.loader.importLibrary('places'),
        this.loader.importLibrary('maps'),
        this.loader.importLibrary('marker'),
      ]);
      
      this.placesLibrary = places;
      this.mapsLibrary = maps;
      this.markerLibrary = marker;
      this.googleMapsLoaded = true;
    } catch (error) {
      throw error;
    }
  }

  //Verifica si Google Maps está cargado
  isGoogleMapsLoaded(): boolean {
    return this.googleMapsLoaded;
  }

  //Obtiene la instancia de Google Maps (fallback para compatibilidad)
  getGoogleMaps(): any {
    if (!this.googleMapsLoaded) {
      throw new Error('Google Maps not loaded. Call loadGoogleMaps() first.');
    }
    return (window).google;
  }

  //Obtiene la librería de Places
  getPlacesLibrary(): any {
    if (!this.googleMapsLoaded || !this.placesLibrary) {
      throw new Error('Places library not loaded. Call loadGoogleMaps() first.');
    }
    return this.placesLibrary;
  }

  //Obtiene la librería de Maps
  getMapsLibrary(): any {
    if (!this.googleMapsLoaded || !this.mapsLibrary) {
      throw new Error('Maps library not loaded. Call loadGoogleMaps() first.');
    }
    return this.mapsLibrary;
  }

  //Obtiene la librería de Marker
  getMarkerLibrary(): any {
    if (!this.googleMapsLoaded || !this.markerLibrary) {
      throw new Error('Marker library not loaded. Call loadGoogleMaps() first.');
    }
    return this.markerLibrary;
  }

  //Crea un mapa
  createMap(element: HTMLElement, options: any): any {
    const { Map } = this.getMapsLibrary();
    return new Map(element, options);
  }

  //Crea un marcador
  createMarker(options: any): any {
    const { Marker } = this.getMarkerLibrary();
    return new Marker(options);
  }

  //Crea un geocoder
  createGeocoder(): any {
    const google = this.getGoogleMaps();
    return new google.maps.Geocoder();
  }

  //Crea un autocompletado para direcciones
  createAutocomplete(input: HTMLInputElement, options?: any): any {
    const { Autocomplete } = this.getPlacesLibrary();
    return new Autocomplete(input, options);
  }

  //Autocomplete con event listener
  createAutocompleteWithListener(input: HTMLInputElement, options?: any): any {
    const { Autocomplete } = this.getPlacesLibrary();
    const autocomplete = new Autocomplete(input, options);
    
    // Agregar listener para cuando se selecciona un lugar
    autocomplete.addListener('place_changed', (place: any) => {
      console.log('Place selected:', place);
    });
    
    return autocomplete;
  }

  //Calcula la distancia entre dos puntos
  calculateDistance(origin: google.maps.LatLng, destination: google.maps.LatLng): Promise<number> {
    const google = this.getGoogleMaps();
    const service = new google.maps.DistanceMatrixService();

    return new Promise((resolve, reject) => {
      service.getDistanceMatrix({
        origins: [origin],
        destinations: [destination],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC
      }, (response: any, status: any) => {
        if (status === 'OK') {
          const distance = response.rows[0].elements[0].distance.value;
          resolve(distance);
        } else {
          reject(new Error('Distance calculation failed'));
        }
      });
    });
  }

  //Convierte una dirección a coordenadas
  geocodeAddress(address: string): Promise<google.maps.LatLng> {
    const geocoder = this.createGeocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results: any, status: any) => {
        if (status === 'OK') {
          resolve(results[0].geometry.location);
        } else {
          reject(new Error('Geocoding failed'));
        }
      });
    });
  }

  //Busca predicciones usando AutocompleteSuggestion (recomendado desde marzo 2025)
  getPlacePredictions(query: string, options?: any): Promise<any[]> {
    const { AutocompleteSuggestion } = this.getPlacesLibrary();
    const svc = new AutocompleteSuggestion();
    return new Promise((resolve, reject) => {
      const request = {
        input: query,
        ...options,
      };
      svc.getPlacePredictions(request, (predictions: any, status: any) => {
        if (status === 'OK') {
          resolve(predictions || []);
        } else {
          reject(new Error('Autocomplete predictions failed: ' + status));
        }
      });
    });
  }

  //Nuevo modelo de Autocomplete con token de sesión usando AutocompleteSuggestion
  getPlacePredictionsNew(query: string): Promise<any[]> {
    const { AutocompleteSuggestion, AutocompleteSessionToken } = this.getPlacesLibrary();
    const svc = new AutocompleteSuggestion();
    const session = new AutocompleteSessionToken();
    return new Promise((resolve, reject) => {
      svc.getPlacePredictions({ input: query, sessionToken: session }, (predictions: any, status: any) => {
        if (status === 'OK') {
          resolve(predictions || []);
        } else {
          reject(new Error('Autocomplete (new/session) failed: ' + status));
        }
      });
    });
  }

  //Geocodifica una dirección y devuelve el resultado completo
  geocodeAddressComplete(address: string): Promise<google.maps.GeocoderResult> {
    const geocoder = this.createGeocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results: any, status: any) => {
        if (status === 'OK') {
          resolve(results[0]);
        } else {
          reject(new Error('Geocoding failed'));
        }
      });
    });
  }

  //Obtiene detalles de un placeId usando API Place (new)
  async fetchPlaceDetails(placeId: string): Promise<{ lat: number; lng: number; formattedAddress: string }> {
    const { Place } = this.getPlacesLibrary();
    try {
      const place = new Place({ id: placeId });
      await place.fetchFields({ fields: ['formattedAddress', 'location'] });
      const formattedAddress = place.formattedAddress || '';
      const loc = place.location;
      const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
      const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
      return { lat, lng, formattedAddress };
    } catch (e) {
      return new Promise((resolve, reject) => {
        const { PlacesService } = this.getPlacesLibrary();
        const svc = new PlacesService(document.createElement('div'));
        svc.getDetails({ placeId, fields: ['formatted_address', 'geometry'] }, (result: any, status: any) => {
          if (status === 'OK' && result) {
            resolve({
              lat: result.geometry.location.lat(),
              lng: result.geometry.location.lng(),
              formattedAddress: result.formatted_address,
            });
          } else {
            reject(new Error('getDetails failed: ' + status));
          }
        });
      });
    }
  }

  //Convierte coordenadas a dirección
  reverseGeocode(latLng: { lat: number; lng: number }): Promise<string> {
    const geocoder = this.createGeocoder();
    const google = this.getGoogleMaps();
    const location = new google.maps.LatLng(latLng.lat, latLng.lng);

    return new Promise((resolve, reject) => {
      geocoder.geocode({ location }, (results: any, status: any) => {
        if (status === 'OK') {
          resolve(results[0].formatted_address);
        } else {
          reject(new Error('Reverse geocoding failed'));
        }
      });
    });
  }
} 